import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { useTriggerRestore, useClusterBackups } from "@/hooks/use-backup";
import type { CNPGCluster } from "@/api/backup";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@fox1ck-org/ui";

interface Props {
  clusters: CNPGCluster[];
  onClose: () => void;
}

export function RestoreDialog({ clusters, onClose }: Props) {
  const [selectedCluster, setSelectedCluster] = useState<CNPGCluster | null>(clusters[0] ?? null);
  const [usePITR, setUsePITR] = useState(false);
  const [pitrTime, setPitrTime] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const { data: backupsData } = useClusterBackups(selectedCluster?.namespace ?? "", selectedCluster?.name ?? "");
  const backups = backupsData?.backups ?? [];
  const trigger = useTriggerRestore();

  const handleClusterChange = (value: string) => {
    const c = clusters.find((c) => `${c.namespace}/${c.name}` === value);
    if (c) setSelectedCluster(c);
  };

  const handleSubmit = () => {
    if (!selectedCluster) return;
    trigger.mutate(
      {
        clusterName: selectedCluster.name,
        namespace: selectedCluster.namespace,
        recoveryTargetTime: usePITR && pitrTime ? new Date(pitrTime).toISOString() : undefined,
      },
      {
        onSuccess: () => { toast.success("Восстановление запущено"); onClose(); },
        onError: (err) => toast.error(err.message),
      }
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Восстановление из копии</DialogTitle>
          <DialogDescription>Кластер восстановления из копии или на момент времени.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm text-muted-foreground mb-1 block">Кластер</label>
            <Select value={selectedCluster ? `${selectedCluster.namespace}/${selectedCluster.name}` : ""} onValueChange={handleClusterChange}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {clusters.map((c) => (
                  <SelectItem key={`${c.namespace}/${c.name}`} value={`${c.namespace}/${c.name}`}>
                    {c.name} ({c.namespace})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {backups.length > 0 && (
            <div className="rounded-md bg-muted/50 p-3">
              <p className="text-xs text-muted-foreground mb-2">Available backups ({backups.length}):</p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {backups.slice(0, 5).map((b) => (
                  <div key={b.name} className="flex items-center justify-between text-xs">
                    <span className="text-foreground font-mono">{b.name}</span>
                    <Badge variant={b.phase === "completed" ? "ok" : "warn"} className="text-[10px]">{b.phase}</Badge>
                  </div>
                ))}
              </div>
              {selectedCluster?.firstRecoverabilityPoint && (
                <p className="text-xs text-muted-foreground mt-2">
                  Восстановимо с {new Date(selectedCluster.firstRecoverabilityPoint).toLocaleString()}
                </p>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <label className="text-sm text-foreground">Восстановление на момент времени</label>
              <p className="text-xs text-muted-foreground">Восстановить на конкретное время</p>
            </div>
            <button
              onClick={() => setUsePITR(!usePITR)}
              className={`w-10 h-6 rounded-full transition-colors ${usePITR ? "bg-primary" : "bg-muted"}`}
            >
              <div className={`w-4 h-4 bg-white rounded-full transition-transform mx-1 ${usePITR ? "translate-x-4" : ""}`} />
            </button>
          </div>

          {usePITR && (
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Время восстановления</label>
              <Input type="datetime-local" value={pitrTime} onChange={(e) => setPitrTime(e.target.value)} />
            </div>
          )}

          <div className="flex items-start gap-2 rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
            <AlertTriangle className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0" />
            <p className="text-xs text-yellow-400">
              Будет создан временный кластер восстановления. Исходный кластер не меняется.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="confirm-restore" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="rounded" />
            <label htmlFor="confirm-restore" className="text-sm text-muted-foreground">
              Понимаю, что будет создан временный кластер восстановления
            </label>
          </div>

          {trigger.error && <p className="text-sm text-destructive">{trigger.error.message}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Отмена</Button>
          <Button onClick={handleSubmit} disabled={!confirmed || !selectedCluster || trigger.isPending || (usePITR && !pitrTime)}>
            {trigger.isPending ? "Запускаем…" : "Запустить восстановление"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
